import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import Sidebar from "../Components/Sidebar";
import BottomBar from "../Components/BottomBar";
import TopBar from "../Components/TopBar";
import {
    getAllElections,
    registerElection,
    updateElection,
} from "../services/ElectionService";
import { Menu, CircleCheck } from "lucide-react";

const RegisterElectionPage = () => {
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        description: "",
        electionDate: "",
        candidateCount: "",
        voterCount: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const isEdit = searchParams.get("edit") === "true";
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

    // Prefill form if editing
    useEffect(() => {
        if (isEdit && location.state) {
            const election = location.state;
            setFormData({
                id: election.id || "",
                name: election.name || "",
                description: election.description || "",
                electionDate: election.electionDate || "",
                candidateCount: election.candidateCount || "",
                voterCount: election.voterCount || "",
            });
        }
    }, [isEdit, location.state]);

    // Handle responsive sidebar
    useEffect(() => {
        // Handle responsive sidebar on window resize
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);

        // Listen for sidebar toggle events from Sidebar component
        const handleSidebarToggle = (event) => {
            if (event.detail && typeof event.detail.expanded === "boolean") {
                setIsSidebarOpen(event.detail.expanded);
            }
        };

        window.addEventListener("sidebarToggle", handleSidebarToggle);

        // Cleanup listeners
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener("sidebarToggle", handleSidebarToggle);
        };
    }, []);

    const toggleSidebar = () => {
        const newState = !isSidebarOpen;
        setIsSidebarOpen(newState);
        // Dispatch the custom event to sync with Sidebar component
        window.dispatchEvent(
            new CustomEvent("sidebarToggle", { detail: { expanded: newState } })
        );
    };

    // Handle input changes
    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    // Validate the form
    const validateForm = () => {
        const errors = {};
        if (!formData.id.trim()) {
            errors.id = "ID is required.";
        }
        if (!formData.name.trim()) {
            errors.name = "Name of the vote is required.";
        }
        if (!formData.electionDate) {
            errors.electionDate = "Election date is required.";
        }
        if (!formData.description.trim()) {
            errors.description = "Description is required.";
        }
        if (!formData.candidateCount || parseInt(formData.candidateCount, 10) <= 0) {
            errors.candidateCount = "Candidate count must be greater than 0.";
        }
        if (!formData.voterCount || parseInt(formData.voterCount, 10) <= 0) {
            errors.voterCount = "Voter count must be greater than 0.";
        }
        return errors;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({});
        try {
            if (isEdit) {
                await updateElection(formData.id, {
                    newId: formData.id,
                    name: formData.name,
                    description: formData.description,
                    electionDate: formData.electionDate,
                    candidateCount: formData.candidateCount,
                    voterCount: formData.voterCount,
                });
                toast.success("Election updated successfully!");
            } else {
                await registerElection({
                    id: formData.id,
                    name: formData.name,
                    description: formData.description,
                    electionDate: formData.electionDate,
                    candidateCount: formData.candidateCount,
                    voterCount: formData.voterCount,
                });
                toast.success("Election registered successfully!");
            }
            // Navigate back to the Elections list to fetch updated data
            navigate("/Elections");
        } catch (error) {
            console.error("Error saving election:", error);
            toast.error("Failed to save election.");
        }
    };

    const handleClear = () => {
        setFormData({
            id: "",
            name: "",
            description: "",
            electionDate: "",
            candidateCount: "",
            voterCount: "",
        });
        setFormErrors({});
    };

    // Updated contentClass to properly handle scrolling
    const contentClass = isSidebarOpen
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    // Inner container class for centering content without adding additional scrollbars
    const innerContainerClass = !isSidebarOpen
        ? "w-full max-w-4xl"
        : "w-full";

    return (
        <div className="flex flex-col min-h-screen bg-bgColor relative">
            <TopBar>
                <button
                    onClick={toggleSidebar}
                    className="p-2 mr-2"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
            </TopBar>

            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={`flex-1 transition-all duration-300 mt-16 pb-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 ${innerContainerClass}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-5">
                        <div className="mt-2 md:mt-5">
                            <h2 className="text-xl md:text-2xl font-bold">
                                {isEdit ? "Edit Election" : "Register Election"}
                            </h2>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 w-full mb-10">
                        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Election ID</label>
                                <input
                                    type="text"
                                    name="id"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter election ID"
                                    value={formData.id}
                                    onChange={handleChange}
                                />
                                {formErrors.id && <p className="text-red-500 text-xs mt-1">{formErrors.id}</p>}
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Name of the vote</label>
                                <input
                                    type="text"
                                    name="name"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter vote name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Date of the election</label>
                                <input
                                    type="date"
                                    name="electionDate"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    value={formData.electionDate}
                                    onChange={handleChange}
                                />
                                {formErrors.electionDate && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.electionDate}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Description</label>
                                <textarea
                                    name="description"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter description"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleChange}
                                />
                                {formErrors.description && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Amount of candidates</label>
                                <input
                                    type="number"
                                    name="candidateCount"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter number of candidates"
                                    value={formData.candidateCount}
                                    onChange={handleChange}
                                />
                                {formErrors.candidateCount && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.candidateCount}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Amount of voters</label>
                                <input
                                    type="number"
                                    name="voterCount"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter number of voters"
                                    value={formData.voterCount}
                                    onChange={handleChange}
                                />
                                {formErrors.voterCount && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.voterCount}</p>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row justify-center sm:justify-end w-full space-y-3 sm:space-y-0 sm:space-x-4 mt-6">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="bg-gray-300 text-black text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-400 transition w-full sm:w-auto"
                                >
                                    Clear
                                </button>
                                <button
                                    type="submit"
                                    className="bg-black text-white text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-700 transition flex items-center justify-center w-full sm:w-auto"
                                >
                                    Confirm
                                    <CircleCheck className="ml-2" size={window.innerWidth < 768 ? 16 : 20} />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="fixed bottom-0 left-0 right-0 z-10">
                <BottomBar />
            </div>
            <Toaster />
        </div>
    );
};

export default RegisterElectionPage;